import { useMemo } from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import type { ImageLoader, ImageProps } from 'next/legacy/image';
import NextImage from 'next/legacy/image';
import type { CloudinaryProps } from '$util/image/cloudinary';
import { cloudinaryLoader, getCloudinarySrc } from '$util/image/cloudinary';
import type { MediaQueryBreakpoint } from '$util/theme/withResponsive';
import { getDisplayClassName } from '$util/theme/withResponsive';

interface ImageDefaultProps extends Omit<ImageProps, 'width' | 'height'> {
  /**
   * ImageProps uses `SafeNumber` which does not match `string` type.
   */
  readonly width?: number | string;
  readonly height?: number | string;
}

interface ProviderProps extends Pick<ImageDefaultProps, 'src' | 'width' | 'height'> {
  loader?: ImageLoader;
}

export type Props = ImageDefaultProps &
  CloudinaryProps & {
    readonly provider?: 'cloudinary';
    /**
     * Make `layout` prop work with styled-components.
     * @default responsive
     * @see https://nextjs.org/docs/api-reference/next/image#layout
     */
    readonly layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
    /**
     * Used with layout `fill` to automatically fill the size of the image
     */
    readonly fill?: 'natural';
    readonly displayFrom?: MediaQueryBreakpoint;
    readonly displayTo?: MediaQueryBreakpoint;
  };

/**
 * NextJS Image with provider image loader and works with styled-components.
 *
 * @deprecated use `src/atoms/Img`
 */
function ImageNext({
  className,
  provider,
  src,
  width,
  height,
  layout = 'responsive',
  format,
  displayFrom,
  displayTo,
  ...imageProps
}: Props) {
  /**
   * Using styled-components instead results in class mismatch between SSR and CSR
   */
  const style = useMemo(() => {
    // keep size consistent
    const divStyle: Record<string, string> = { display: layout === 'fixed' ? 'inline-block' : 'block' };
    if (width && height) {
      Object.assign(divStyle, {
        width: layout === 'fixed' ? `${width}px` : '100%',
        height: layout === 'fixed' ? `${height}px` : '100%',
      });
    }
    return divStyle;
  }, [width, height, layout]);

  const providerProps = useMemo(() => {
    const props: ProviderProps = { src };
    if (width && height) Object.assign(props, { width, height });
    if (provider === 'cloudinary' && typeof src === 'string') {
      props.src = getCloudinarySrc(src);
      props.loader = cloudinaryLoader({ format });
    }
    return props;
  }, [provider, src, width, height, format]);

  return (
    <span
      className={classNames('Image', getDisplayClassName(displayFrom, displayTo), className)}
      style={style}
    >
      <NextImage {...(imageProps as ImageProps)} {...(providerProps as ImageProps)} layout={layout} />
    </span>
  );
}

/**
 * @deprecated use `src/atoms/Img`
 */
export const Image = styled(ImageNext)`
  ${({ layout, fill }: Props) => {
    switch (layout) {
      case 'fill': {
        if (fill === 'natural') {
          // https://dev.to/tanahmed/next-image-make-image-fill-available-space-272o

          return css`
            > span {
              position: static !important;
              height: 100%;
              img {
                object-fit: contain;
                width: 100% !important;
                position: relative !important;
                height: 100% !important;
              }
            }
          `;
        }
        break;
      }
      case 'fixed':
        return css`
          > span {
            vertical-align: top;
          }
        `;
      default:
        break;
    }
    return null;
  }}
`;
Image.displayName = 'Image';
